/** @jsx jsx */

import React, { forwardRef } from 'react';
import { styled, cssMap, jsx } from '@compiled/react';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { EMPTY_INLINE_EDIT_CONTEXT } from '@atlassian/jira-inline-edit-lite/src/common/constants.tsx';
import { EditButtonHidden } from '@atlassian/jira-inline-edit-lite/src/ui/edit-button-hidden/index.tsx';
import { EditButton } from '@atlassian/jira-inline-edit-lite/src/ui/edit-button/index.tsx';
import type { Props } from './types.tsx';

const borderStyles = cssMap({
	focused: {
		borderColor: token('color.border.focused'),
	},
	invalid: {
		borderColor: token('color.border.danger'),
	},
	copied: {
		borderColor: token('color.border.focused'),
		borderStyle: 'dashed',
	},
	none: {
		borderColor: 'transparent',
	},
});

const getBorderStyle = ({
	isFocusRingVisible,
	isInvalid,
	isCopied,
}: {
	isFocusRingVisible: boolean;
	isInvalid: boolean;
	isCopied: boolean;
}): keyof typeof borderStyles => {
	if (isFocusRingVisible) {
		return 'focused';
	}
	if (isInvalid) {
		return 'invalid';
	}
	if (isCopied) {
		return 'copied';
	}
	return 'none';
};

/**
 * This component is designed to be rendered within the `readView` of a `FieldInlineEditLite` component. This can be
 * rendered in a 'read-only' layout by omitting the `context` prop which is provided by `useInlineEdit` in
 * `@atlassian/jira-inline-edit-lite`. This ensures consistent spacing for consumers when a field is conditionally
 * editable.
 */
const InlineEditReadViewContainer = forwardRef<HTMLDivElement, Props>(
	(
		{
			children,
			context,
			editButtonLabel,
			editButtonRef,
			hideEditButton = true,
			isCopied = false,
			isEditButtonFocused = false,
			isEditInteractionEnabled = true,
			isLoading = false,
			isInvalid = false,
			onClick,
			onMouseDown,
			onEditButtonBlur,
			onEditButtonFocus,
			readViewFitContainerHeight = false,
			hasReadViewTruncateDisabled = false,
		},
		ref,
	) => {
		const isHiddenButtonFocused = hideEditButton && isEditButtonFocused;
		const isFocusRingVisible = isHiddenButtonFocused || isLoading;
		const isEditable = Boolean(context) && isEditInteractionEnabled;

		const borderStyle = fg('jsc_inline_editing_field_refactor')
			? getBorderStyle({
					isFocusRingVisible,
					isInvalid,
					isCopied,
				})
			: undefined;

		return (
			<Container
				data-testid="issue-field-inline-edit-read-view-container.ui.container"
				data-vc="issue-field-inline-edit-read-view-container"
				readViewFitContainerHeight={readViewFitContainerHeight}
				isEditable={isEditable}
				isFocusRingVisible={isFocusRingVisible}
				isInvalid={isInvalid}
				onClick={onClick}
				onMouseDown={onMouseDown}
				role="presentation"
				ref={ref}
				css={borderStyle && borderStyles[borderStyle]}
				hasReadViewTruncateDisabled={hasReadViewTruncateDisabled}
				// Consumers may currently update non-visual styles dynamically based off isEditable prop.
				// Here we allow consumers to exclude the attachment of edit styles from VC calculation.
				// Learn more about why this has been introduced https://jplat.atlassian.net/browse/EM-9211
				data-vc-nvs={fg('ignore_inline_edit_cell')}
			>
				{hideEditButton && (
					<EditButtonHidden
						ref={editButtonRef}
						context={context ?? EMPTY_INLINE_EDIT_CONTEXT}
						isDisabled={!isEditable}
						label={editButtonLabel}
						onBlur={onEditButtonBlur}
						onFocus={onEditButtonFocus}
					/>
				)}
				{isLoading ? (
					<>
						<LoadingOverlay>{children}</LoadingOverlay>
						<SpinnerContainer>
							<Spinner />
						</SpinnerContainer>
					</>
				) : (
					children
				)}
				{!hideEditButton && (
					<EditButton
						ref={editButtonRef}
						context={context ?? EMPTY_INLINE_EDIT_CONTEXT}
						isDisabled={!isEditable}
						label={editButtonLabel}
						onBlur={onEditButtonBlur}
						onFocus={onEditButtonFocus}
						// In future providing CSS variables *may* be something that makes its way into the design system, but
						// for now this is an acceptable override to prevent managing additional state with JS.
						// More context here https://atlassian.slack.com/archives/C060GTVV988/p1703214250865779?thread_ts=1703212675.166419&cid=C060GTVV988
						/* eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766, */
						style={editButtonStyles}
					/>
				)}
			</Container>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{
	readViewFitContainerHeight: boolean;
	isEditable: boolean;
	isFocusRingVisible: boolean;
	isInvalid: boolean;
	hasReadViewTruncateDisabled: boolean;
}>(
	{
		borderWidth: '2px',
		borderStyle: 'solid',
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
		borderRadius: token('border.radius', '3px'),
		paddingTop: 0,
		paddingRight: token('space.075'),
		paddingBottom: 0,
		paddingLeft: token('space.075'),
		display: 'flex',
		gap: token('space.100'),
		alignItems: 'center',
		boxSizing: 'border-box',
		maxWidth: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		minHeight: (props) => (props.readViewFitContainerHeight ? '100%' : '32px'),
		position: 'relative',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		overflow: (props) => (props.hasReadViewTruncateDisabled ? 'visible' : 'hidden'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		textOverflow: (props) => (props.hasReadViewTruncateDisabled ? 'unset' : 'ellipsis'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.isEditable && {
			transition: 'backgroundColor 0.2s',
			'&:hover': {
				backgroundColor: token('color.background.neutral.subtle.hovered'),
			},
			'--edit-button-opacity': 0,
			'&:hover,&:focus-within': {
				'--edit-button-opacity': 1,
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.isFocusRingVisible &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		!fg('jsc_inline_editing_field_refactor') && {
			borderColor: token('color.border.focused'),
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.isInvalid &&
		!props.isFocusRingVisible &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		!fg('jsc_inline_editing_field_refactor') && {
			borderColor: token('color.border.danger'),
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		!props.isInvalid &&
		!props.isFocusRingVisible &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		!fg('jsc_inline_editing_field_refactor') && {
			borderColor: 'transparent',
		},
);

const editButtonStyles = {
	flexShrink: 0,
	marginLeft: 'auto',
	opacity: 'var(--edit-button-opacity, 0)',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingOverlay = styled.div({
	pointerEvents: 'none',
	opacity: token('opacity.loading'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerContainer = styled.div({
	position: 'absolute',
	// Space tokens do not make sense for this positioning.
	left: '50%',
	top: '50%',
	transform: 'translate(-50%, -50%)',
});

export default InlineEditReadViewContainer;
